import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Handshake, Users, Globe, Building2, TrendingUp, Target, CheckCircle, ArrowUpRight, Layers, Zap, Award, Network, ArrowRight, DollarSign, Shield, Rocket } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLREVU_LOGO = "https://www.callrevu.com/wp-content/uploads/2024/12/CR_Logo_Wordmark_White_RGB.png";
const CALLDRIP_LOGO = "https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      headline: "Partner Relationships",
      highlight: "That Drive Growth",
      subhead: "Partner Overview",
      tagline: "From introductions → integrations → revenue impact"
    }
  },
  {
    id: 2,
    type: "philosophy",
    content: {
      label: "LET'S BE HONEST",
      headline: "Why Partnerships Matter",
      reality: [
        "Most \"partnerships\" are logos on a slide",
        "Real ones open doors",
        "Real ones shorten sales cycles",
        "Real ones create trust before we show up"
      ],
      philosophy: "If the partner wins, we win. If the dealer wins, everyone stays."
    }
  },
  {
    id: 3,
    type: "partnerTypes",
    content: {
      label: "NOT ALL PARTNERS ARE EQUAL",
      headline: "What We Mean by \"Partner\"",
      types: [
        { 
          level: "1",
          title: "Strategic Introducers", 
          desc: "Open the door",
          color: "#3b82f6"
        },
        { 
          level: "2",
          title: "Operational Partners", 
          desc: "Integrated into workflow",
          color: "#8b5cf6"
        },
        { 
          level: "3",
          title: "Enterprise Relationships", 
          desc: "Scale across rooftops",
          color: "#f97316"
        },
      ],
      goal: "The goal is simple: move every partner UP this ladder"
    }
  },
  {
    id: 4,
    type: "caseStudy",
    content: {
      label: "CASE STUDY",
      name: "#DCDW",
      role: "International Relationship Builder",
      location: "Netherlands",
      color: "#3b82f6",
      story: [
        "Brought outside-market perspective",
        "Helped position us beyond U.S. retail norms",
        "Opened conversations we don't get on our own"
      ],
      insight: "Partnerships expand how you're perceived, not just who you reach"
    }
  },
  {
    id: 5,
    type: "caseStudy",
    content: {
      label: "CASE STUDY",
      name: "TracTru → Kubota",
      role: "Connector into OEM Ecosystem",
      location: "Equipment Dealers",
      color: "#22c55e",
      story: [
        "TracTru introduced us to Kubota",
        "Created alignment with dealer needs + OEM expectations",
        "Positioned us as more than just a \"tool\""
      ],
      results: [
        "Entry into OEM-level conversations",
        "Foundation for co-op opportunities + scale"
      ],
      insight: "The right partner doesn't just bring leads... they bring distribution"
    }
  },
  {
    id: 6,
    type: "caseStudy",
    content: {
      label: "CASE STUDY",
      name: "The Halfman Group",
      role: "Phone Skills Training Partner",
      location: "10+ Year Relationship • 30–40 Shared Accounts",
      color: "#f97316",
      story: [
        "Partnership started ~10 years ago",
        "They train dealers on phone skills... we give them the data",
        "Customized scripts for internet-lead calls (handled very differently than standard inbound)",
        "Also leverage Calldrip for click-to-call training"
      ],
      results: [
        "30–40 joint accounts across our two platforms",
        "Insights into both inbound AND initial internet-lead calls",
        "Better-trained phone teams = higher set rates for dealers"
      ],
      insight: "The best partners don't just sell alongside you — they make the product work better in the wild"
    }
  },
  {
    id: 7,
    type: "pattern",
    content: {
      label: "THE PATTERN",
      headline: "What Makes These Work",
      principles: [
        {
          number: "1",
          title: "Trust Before Transaction",
          desc: "These weren't cold deals. They were relationship-driven.",
          color: "#3b82f6"
        },
        {
          number: "2",
          title: "Clear Value Alignment",
          desc: "Not \"what do we sell\" but \"what problem are we solving together\"",
          color: "#22c55e"
        },
        {
          number: "3",
          title: "Speed + Execution",
          desc: "When opportunity shows up... we move",
          color: "#f97316"
        }
      ],
      callout: "Most companies miss #3 and wonder why partnerships stall"
    }
  },
  {
    id: 8,
    type: "amplify",
    content: {
      label: "THE MULTIPLIER EFFECT",
      headline: "Partners Don't Just Introduce",
      subtitle: "They Amplify",
      benefits: [
        { icon: "zap", text: "Faster adoption at the store level" },
        { icon: "users", text: "Better engagement with leadership" },
        { icon: "shield", text: "Stronger long-term retention" },
      ],
      punchline: "They validate the message before we even say it"
    }
  },
  {
    id: 9,
    type: "strategy",
    content: {
      label: "LOOKING AHEAD",
      headline: "Partner Strategy Going Forward",
      initiatives: [
        {
          title: "Deeper OEM Relationships",
          desc: "Kubota is just the start",
          icon: "building"
        },
        {
          title: "More Enterprise Dealer Groups",
          desc: "Multi-rooftop partnerships expanded",
          icon: "network"
        },
        {
          title: "Technology + Marketing Partners",
          desc: "Integrations that increase stickiness",
          icon: "layers"
        },
        {
          title: "Scalable Partner Programs",
          desc: "Not one-offs... repeatable systems",
          icon: "rocket"
        }
      ]
    }
  },
  {
    id: 10,
    type: "bottomLine",
    content: {
      label: "THE BOTTOM LINE",
      levels: [
        { label: "Good partnerships", result: "Introductions", color: "#3b82f6" },
        { label: "Great partnerships", result: "Revenue", color: "#22c55e" },
        { label: "Elite partnerships", result: "Scale + Defensibility", color: "#f97316" }
      ],
      closing: "That's what we're building."
    }
  },
  {
    id: 11,
    type: "execution",
    content: {
      label: "ONE MORE THING",
      headline: "This Only Works If...",
      points: [
        "Fast follow-up",
        "Consistent messaging",
        "Clear value delivery"
      ],
      truth: "Partnerships don't save bad execution",
      emphasis: "They amplify good execution"
    }
  },
  {
    id: 12,
    type: "closing",
    content: {
      headline: "Building Partnerships",
      highlight: "That Scale",
      message: "The relationships we've built aren't just about today's revenue. They're about tomorrow's defensibility."
    }
  }
];

// Icon component mapper
const IconComponent = ({ name, size = 24, className = "" }) => {
  const icons = {
    handshake: Handshake,
    users: Users,
    globe: Globe,
    building: Building2,
    trending: TrendingUp,
    target: Target,
    check: CheckCircle,
    arrowUp: ArrowUpRight,
    layers: Layers,
    zap: Zap,
    award: Award,
    network: Network,
    arrow: ArrowRight,
    dollar: DollarSign,
    shield: Shield,
    rocket: Rocket,
  };
  const Icon = icons[name] || Target;
  return <Icon size={size} className={className} />;
};

// Slide Renderers
const TitleSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <div className="flex items-center gap-8 mb-12">
      <img src={CALLREVU_LOGO} alt="CallRevu" className="h-12 opacity-90" />
      <div className="w-px h-10 bg-white/20" />
      <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 opacity-90" />
    </div>
    <h1 className="text-4xl md:text-6xl font-light text-white mb-2">
      {content.headline}
    </h1>
    <h1 className="text-5xl md:text-7xl font-bold text-cyan-400 mb-4">
      {content.highlight}
    </h1>
    <h2 className="text-2xl md:text-3xl font-light text-white/80 mb-8">
      {content.subhead}
    </h2>
    <p className="text-lg text-white/60">{content.tagline}</p>
  </div>
);

const PhilosophySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex flex-col justify-center">
      <div className="space-y-4 mb-10">
        {content.reality.map((point, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-red-400' : 'bg-green-400'}`} />
            <span className={`text-xl ${i === 0 ? 'text-white/60' : 'text-white'}`}>{point}</span>
          </div>
        ))}
      </div>
      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl p-6 border border-cyan-500/30">
        <p className="text-xl text-white font-semibold text-center">
          <span className="text-cyan-400">Our philosophy:</span> {content.philosophy}
        </p>
      </div>
    </div>
  </div>
);

const PartnerTypesSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex items-center justify-center">
      <div className="flex items-end gap-4">
        {content.types.map((type, i) => (
          <div key={i} className="flex items-end gap-4">
            <div 
              className="rounded-xl p-6 text-center border-2 transition-all flex flex-col"
              style={{ 
                backgroundColor: type.color + "20",
                borderColor: type.color,
                height: `${180 + i * 60}px`,
                width: '220px'
              }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mb-4 mx-auto" style={{ backgroundColor: type.color }}>
                {type.level}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{type.title}</h3>
              <p className="text-white/60 text-sm">{type.desc}</p>
            </div>
            {i < content.types.length - 1 && (
              <ArrowUpRight className="text-white/30 mb-20" size={24} style={{ transform: 'rotate(45deg)' }} />
            )}
          </div>
        ))}
      </div>
    </div>
    <div className="mt-6 text-center">
      <p className="text-xl text-cyan-400 font-semibold">{content.goal}</p>
    </div>
  </div>
);

const CaseStudySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <div className="flex items-center gap-4 mb-6">
      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: content.color }}>
        <Users size={32} className="text-white" />
      </div>
      <div>
        <h2 className="text-3xl md:text-4xl font-bold text-white">{content.name}</h2>
        <p className="text-white/60">{content.role} • {content.location}</p>
      </div>
    </div>
    <div className="flex-1 grid grid-cols-2 gap-8">
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">What Happened</h3>
        <div className="space-y-3">
          {content.story.map((point, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-1" />
              <span className="text-white/80">{point}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col">
        {content.results && (
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-4">
            <h3 className="text-lg font-semibold text-white mb-4">Results</h3>
            <div className="space-y-3">
              {content.results.map((result, i) => (
                <div key={i} className="flex items-start gap-3">
                  <TrendingUp size={18} style={{ color: content.color }} className="flex-shrink-0 mt-1" />
                  <span className="text-white/80">{result}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="bg-gradient-to-r rounded-xl p-6 border flex-1 flex items-center" style={{ backgroundColor: content.color + "20", borderColor: content.color + "50" }}>
          <p className="text-lg text-white">
            <span className="font-bold" style={{ color: content.color }}>Key Insight:</span><br/>
            {content.insight}
          </p>
        </div>
      </div>
    </div>
  </div>
);

const PatternSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 space-y-6">
      {content.principles.map((principle, i) => (
        <div key={i} className="flex items-center gap-6">
          <div 
            className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold text-white flex-shrink-0"
            style={{ backgroundColor: principle.color }}
          >
            {principle.number}
          </div>
          <div className="flex-1 bg-white/5 rounded-xl p-5 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-1">{principle.title}</h3>
            <p className="text-white/60">{principle.desc}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-6 text-center">
      <p className="text-xl text-orange-400 font-semibold">{content.callout}</p>
    </div>
  </div>
);

const AmplifySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">{content.headline}</h2>
    <p className="text-2xl text-cyan-400 mb-10">{content.subtitle}</p>
    <div className="flex-1 flex flex-col justify-center">
      <div className="space-y-5 max-w-2xl mx-auto w-full mb-10">
        {content.benefits.map((benefit, i) => (
          <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
            <IconComponent name={benefit.icon} size={28} className="text-cyan-400 flex-shrink-0" />
            <span className="text-white text-lg">{benefit.text}</span>
          </div>
        ))}
      </div>
      <div className="text-center">
        <p className="text-2xl font-bold text-white">
          <span className="text-cyan-400">→</span> {content.punchline}
        </p>
      </div>
    </div>
  </div>
);

const StrategySlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 grid grid-cols-2 gap-6">
      {content.initiatives.map((item, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-6 border border-white/10 flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <IconComponent name={item.icon} size={24} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
            <p className="text-white/60">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const BottomLineSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="space-y-6 mb-12">
        {content.levels.map((level, i) => (
          <div key={i} className="flex items-center gap-6">
            <div className="w-64 text-right">
              <span className="text-xl text-white/80">{level.label}</span>
            </div>
            <div className="text-3xl font-bold" style={{ color: level.color }}>=</div>
            <div className="w-64">
              <span className="text-2xl font-bold" style={{ color: level.color }}>{level.result}</span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-3xl font-bold text-white">{content.closing}</p>
    </div>
  </div>
);

const ExecutionSlide = ({ content }) => (
  <div className="flex flex-col h-full px-12 py-8">
    <div className="text-cyan-400 text-sm font-semibold tracking-widest mb-4">{content.label}</div>
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">{content.headline}</h2>
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="space-y-4 mb-10">
        {content.points.map((point, i) => (
          <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl px-8 py-4 border border-white/10">
            <CheckCircle size={24} className="text-green-400" />
            <span className="text-xl text-white">{point}</span>
          </div>
        ))}
      </div>
      <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-8 border border-orange-500/30 text-center max-w-2xl">
        <p className="text-xl text-white/80 mb-2">{content.truth}</p>
        <p className="text-2xl font-bold text-orange-400">{content.emphasis}</p>
      </div>
    </div>
  </div>
);

const ClosingSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 pt-12">
    <div className="flex items-center gap-8 mb-12">
      <img src={CALLREVU_LOGO} alt="CallRevu" className="h-12 opacity-90" />
      <div className="w-px h-10 bg-white/20" />
      <img src={CALLDRIP_LOGO} alt="Calldrip" className="h-10 opacity-90" />
    </div>
    <h1 className="text-4xl md:text-5xl font-light text-white mb-2">
      {content.headline}
    </h1>
    <h1 className="text-5xl md:text-6xl font-bold text-cyan-400 mb-10">
      {content.highlight}
    </h1>
    <p className="text-xl text-white/80 max-w-2xl">{content.message}</p>
  </div>
);

// Main slide renderer
const SlideRenderer = ({ slide }) => {
  const renderers = {
    title: TitleSlide,
    philosophy: PhilosophySlide,
    partnerTypes: PartnerTypesSlide,
    caseStudy: CaseStudySlide,
    pattern: PatternSlide,
    amplify: AmplifySlide,
    strategy: StrategySlide,
    bottomLine: BottomLineSlide,
    execution: ExecutionSlide,
    closing: ClosingSlide,
  };
  
  const Renderer = renderers[slide.type];
  return Renderer ? <Renderer content={slide.content} /> : null;
};

export default function PresentationsPartners() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      } else if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide, toggleFullscreen, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1620] flex flex-col">
      {/* Slide Container */}
      <div className="flex-1 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-[#0a1620] via-[#0d1f30] to-[#0a1620]"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(34, 211, 238, 0.06) 0%, transparent 40%),
              radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 40%)
            `
          }}
        />
        
        {/* Current Slide */}
        <div className="relative h-full">
          <SlideRenderer slide={slides[currentSlide]} />
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronLeft className="text-white" size={24} />
        </button>
        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
        >
          <ChevronRight className="text-white" size={24} />
        </button>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#050d14] border-t border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm">
            Slide {currentSlide + 1} of {slides.length}
          </span>
          <div className="flex gap-1">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentSlide ? "bg-cyan-400 w-6" : "bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/40 text-xs">
            Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">F</kbd> for fullscreen
            <span className="mx-2">|</span>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60">←</kbd>
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60 ml-1">→</kbd> to navigate
          </span>
          <PresentationDownloadButton
            slides={slides}
            renderSlide={(s) => <SlideRenderer slide={s} />}
            filename="Partner-Relationships"
            bgColor="#0a1620"
          />
          <button
            onClick={toggleFullscreen}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {isFullscreen ? "Exit Fullscreen" : "Present"}
          </button>
        </div>
      </div>
    </div>
  );
}
